import React from 'react';
import dayjs from 'dayjs';
import './PayslipTemplateRenderer.css';

const DEFAULT_LAYOUT = {
  header: [],
  sections: [],
  footer: [],
  sectionSettings: {
    header: { width: 100, align: 'left', marginTop: 0, marginBottom: 12, marginLeft: 0, marginRight: 0 },
    sections: { width: 100, align: 'left', marginTop: 0, marginBottom: 12, marginLeft: 0, marginRight: 0 },
    footer: { width: 100, align: 'left', marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 }
  }
};

const ZONES = ['header', 'sections', 'footer'];

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `Rs. ${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const numberToWords = (value) => {
  const amount = Math.round(Number(value || 0));
  if (!amount) return 'Rupees zero only';

  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const belowHundred = (num) => (num < 20 ? ones[num] : `${tens[Math.floor(num / 10)]}${num % 10 ? ` ${ones[num % 10]}` : ''}`);
  const belowThousand = (num) => {
    const hundred = Math.floor(num / 100);
    const rest = num % 100;
    return `${hundred ? `${ones[hundred]} hundred` : ''}${hundred && rest ? ' ' : ''}${rest ? belowHundred(rest) : ''}`;
  };

  const crore = Math.floor(amount / 10000000);
  const lakh = Math.floor((amount % 10000000) / 100000);
  const thousand = Math.floor((amount % 100000) / 1000);
  const rest = amount % 1000;
  const parts = [];

  if (crore) parts.push(`${belowThousand(crore)} crore`);
  if (lakh) parts.push(`${belowThousand(lakh)} lakh`);
  if (thousand) parts.push(`${belowThousand(thousand)} thousand`);
  if (rest) parts.push(belowThousand(rest));

  return `Rupees ${parts.join(' ')} only`;
};

const normalizeItem = (field) => {
  if (!field) return field;

  const fieldId = field.fieldId || field.id;
  const isTable = field.type === 'table';
  const isContainer = field.type === 'container';
  const columnCount = field.columnCount || field.tableColumns || (isTable ? 2 : 1);
  const defaultWidth = Math.floor(100 / columnCount);
  const columnWidths = Array.from(
    { length: columnCount },
    (_, index) => field.columnWidths?.[index] || defaultWidth
  );
  const base = {
    ...field,
    fieldId,
    columnCount,
    columnWidths,
    style: field.style || {}
  };

  if (isContainer) {
    return {
      ...base,
      columns: Array.from(
        { length: columnCount },
        (_, index) => (field.columns?.[index] || []).map(normalizeItem)
      )
    };
  }

  if (isTable) {
    const tableColumns = field.tableColumns || columnCount || 2;
    const tableRows = field.tableRows || 3;
    const tableColumnWidths = Array.from(
      { length: tableColumns },
      (_, index) => field.tableColumnWidths?.[index] || Math.floor(100 / tableColumns)
    );
    const tableCells = Array.from(
      { length: tableRows },
      (_, rowIndex) => Array.from(
        { length: tableColumns },
        (_, columnIndex) => normalizeItem(field.tableCells?.[rowIndex]?.[columnIndex] || null)
      )
    );

    return {
      ...base,
      tableRows,
      tableColumns,
      tableColumnWidths,
      tableCells
    };
  }

  return base;
};

const normalizeLayout = (layout) => {
  const source = layout || DEFAULT_LAYOUT;
  const normalized = ZONES.reduce((acc, zone) => {
    acc[zone] = Array.isArray(source[zone]) ? source[zone].map(normalizeItem) : [];
    return acc;
  }, {});

  normalized.sectionSettings = ZONES.reduce((acc, zone) => {
    acc[zone] = {
      ...DEFAULT_LAYOUT.sectionSettings[zone],
      ...(source.sectionSettings?.[zone] || {})
    };
    return acc;
  }, {});

  return normalized;
};

const isCellCoveredBySpan = (table, targetRow, targetColumn) => {
  for (let rowIndex = 0; rowIndex <= targetRow; rowIndex += 1) {
    for (let columnIndex = 0; columnIndex < (table.tableColumns || 0); columnIndex += 1) {
      if (rowIndex === targetRow && columnIndex === targetColumn) return false;

      const cell = table.tableCells?.[rowIndex]?.[columnIndex];
      const rowSpan = cell?.rowSpan || 1;
      const colSpan = cell?.colSpan || 1;

      if (
        cell &&
        rowIndex <= targetRow &&
        rowIndex + rowSpan > targetRow &&
        columnIndex <= targetColumn &&
        columnIndex + colSpan > targetColumn
      ) {
        return true;
      }
    }
  }

  return false;
};

const getLogoSrc = (organization) => {
  const logo = organization?.logo;
  if (!logo) return null;
  if (typeof logo === 'string') return logo.startsWith('data:') ? logo : `data:${organization.logoMimeType || 'image/png'};base64,${logo}`;
  if (logo?.data) {
    const bytes = new Uint8Array(logo.data);
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return `data:${organization.logoMimeType || 'image/png'};base64,${window.btoa(binary)}`;
  }
  return null;
};

const getFieldStyle = (field) => ({
  fontWeight: field.style?.bold ? 700 : 400,
  fontSize: `${field.style?.fontSize || 12}px`,
  textAlign: field.style?.align || 'left',
  color: field.style?.color || undefined,
  backgroundColor: field.style?.backgroundColor || undefined,
  marginTop: field.style?.marginTop || 0,
  marginBottom: field.style?.marginBottom || 0,
  marginLeft: field.style?.marginLeft || 0,
  marginRight: field.style?.marginRight || 0
});

const buildPayslipValues = (payslip) => {
  const employee = payslip?.employee || {};
  const organization = payslip?.organization || {};
  const department = payslip?.department || employee.department?.name || employee.department || '';
  const totalDeductions = Number(payslip?.pf || 0) + Number(payslip?.nps || 0) + Number(payslip?.tds || 0) + Number(payslip?.otherDeductions || 0);

  return {
    logo: getLogoSrc(organization),
    organizationName: organization.name || '',
    organizationAddress: organization.address || '',
    payslipTitle: 'Pay Slip',
    payslipMonth: payslip?.payrollMonth ? dayjs(payslip.payrollMonth).format('MMMM YYYY') : '',
    employeeId: employee.employeeCode || employee.id || '',
    employeeName: `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
    designation: payslip?.designation || employee.designation || '',
    department,
    joinDate: employee.joinDate ? dayjs(employee.joinDate).format('DD MMM YYYY') : '',
    panNumber: employee.panNumber || '',
    uanNumber: employee.uanNumber || '',
    esiNumber: employee.esiNumber || '',
    pfNumber: employee.pfNumber || '',
    bankName: employee.bankName || '',
    accountNumber: employee.bankAccountNumber || '',
    ifscCode: employee.bankIFSC || '',
    basicSalary: formatCurrency(payslip?.basicSalary),
    hra: formatCurrency(payslip?.hra),
    ta: formatCurrency(payslip?.ta),
    da: formatCurrency(payslip?.da),
    medicalAllowance: formatCurrency(payslip?.medicalAllowance),
    conveyanceAllowance: formatCurrency(payslip?.conveyanceAllowance),
    specialAllowance: formatCurrency(payslip?.specialAllowance),
    bonus: formatCurrency(payslip?.bonus),
    incentive: formatCurrency(payslip?.incentive),
    variablePay: formatCurrency(payslip?.variablePay),
    pf: formatCurrency(payslip?.pf),
    nps: formatCurrency(payslip?.nps),
    professionalTax: formatCurrency(payslip?.professionalTax),
    tds: formatCurrency(payslip?.tds),
    grossWages: formatCurrency(payslip?.grossSalary),
    grossSalary: formatCurrency(payslip?.grossSalary),
    totalDeductions: formatCurrency(totalDeductions),
    netSalary: formatCurrency(payslip?.netSalary),
    amountInWords: numberToWords(payslip?.netSalary),
    workingDays: payslip?.workingDays || '',
    paidDays: payslip?.paidDays || '',
    leavesDays: payslip?.leaveDays || payslip?.leavesDays || '',
    organizationSignature: 'Authorized Signature',
    authorizedBy: payslip?.approver ? `${payslip.approver.firstName || ''} ${payslip.approver.lastName || ''}`.trim() : '',
    generatedDate: payslip?.generatedDate ? dayjs(payslip.generatedDate).format('DD MMM YYYY') : dayjs().format('DD MMM YYYY')
  };
};

const earningsRows = (payslip) => [
  ['Basic Salary', payslip?.basicSalary],
  ['HRA', payslip?.hra],
  ['TA', payslip?.ta],
  ['DA', payslip?.da],
  ['Medical Allowance', payslip?.medicalAllowance],
  ['Conveyance Allowance', payslip?.conveyanceAllowance],
  ['Special Allowance', payslip?.specialAllowance],
  ['Bonus', payslip?.bonus],
  ['Incentive', payslip?.incentive],
  ['Variable Pay', payslip?.variablePay]
].filter(([, value]) => Number(value || 0) !== 0);

const deductionRows = (payslip) => [
  ['PF', payslip?.pf],
  ['NPS', payslip?.nps],
  ['Professional Tax', payslip?.professionalTax],
  ['TDS', payslip?.tds],
  ['Other Deductions', payslip?.otherDeductions]
].filter(([, value]) => Number(value || 0) !== 0);

function PayslipTemplateRenderer({ payslip, template }) {
  const layout = normalizeLayout(template?.layoutConfig);
  const values = buildPayslipValues(payslip);

  const getSectionStyle = (zone) => {
    const settings = layout.sectionSettings?.[zone] || DEFAULT_LAYOUT.sectionSettings[zone];
    const baseStyle = {
      width: `${settings.width || 100}%`,
      marginTop: settings.marginTop || 0,
      marginBottom: settings.marginBottom || 0
    };

    if (settings.align === 'center') {
      return { ...baseStyle, marginLeft: 'auto', marginRight: 'auto' };
    }

    if (settings.align === 'right') {
      return { ...baseStyle, marginLeft: 'auto', marginRight: settings.marginRight || 0 };
    }

    return { ...baseStyle, marginLeft: settings.marginLeft || 0, marginRight: 'auto' };
  };

  const renderValue = (field, zone = 'sections') => {
    const fieldKey = field.fieldId || field.id;

    if (field.type === 'image' || fieldKey === 'logo') {
      return (
        <div
          className="payslip-renderer-logo"
          style={{
            width: field.style?.logoWidth || 120,
            height: field.style?.logoHeight || 54,
            ...getFieldStyle(field)
          }}
        >
          {values.logo ? <img src={values.logo} alt="Organization logo" /> : <span>LOGO</span>}
        </div>
      );
    }

    // Handle custom text fields
    if (field.type === 'customText') {
      return (
        <div className="payslip-renderer-custom-text" style={getFieldStyle(field)}>
          {field.customValue || field.defaultValue || 'Enter your text here'}
        </div>
      );
    }

    // For header zone, show only the value without the label
    if (zone === 'header') {
      return (
        <div className="payslip-renderer-value" style={getFieldStyle(field)}>
          {values[fieldKey] || field.defaultValue || '-'}
        </div>
      );
    }

    return (
      <div className="payslip-renderer-value" style={getFieldStyle(field)}>
        <span>{field.label}</span>
        <strong>{values[fieldKey] || field.defaultValue || '-'}</strong>
      </div>
    );
  };

  const renderGeneratedTable = (title, rows, totalLabel, totalValue) => (
    <table className="payslip-renderer-table">
      <thead>
        <tr>
          <th>{title}</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label}>
            <td>{label}</td>
            <td>{formatCurrency(value)}</td>
          </tr>
        ))}
        <tr>
          <td><strong>{totalLabel}</strong></td>
          <td><strong>{formatCurrency(totalValue)}</strong></td>
        </tr>
      </tbody>
    </table>
  );

  const renderItem = (field, zone = 'sections') => {
    if (field.type === 'container') {
      return (
        <div
          className="payslip-renderer-columns"
          style={{
            gridTemplateColumns: field.columnWidths.map((width) => `${width}fr`).join(' '),
            ...getFieldStyle(field)
          }}
        >
          {field.columns.map((columnFields, index) => (
            <div key={index} className="payslip-renderer-column">
              {columnFields.map((nestedField) => (
                <div key={nestedField.templateItemId || nestedField.fieldId} className="payslip-renderer-field">
                  {renderItem(nestedField, zone)}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }

    if (field.fieldId === 'earningsTable') {
      return renderGeneratedTable('Earnings', earningsRows(payslip), 'Gross Salary', payslip?.grossSalary);
    }

    if (field.fieldId === 'deductionsTable') {
      const totalDeductions = Number(payslip?.pf || 0) + Number(payslip?.nps || 0) + Number(payslip?.tds || 0) + Number(payslip?.otherDeductions || 0);
      return renderGeneratedTable('Deductions', deductionRows(payslip), 'Total Deductions', totalDeductions);
    }

    if (field.type === 'table') {
      //why expand header fields are showing in preview, delete that code from here and check if it is working fine, if it is working fine then remove that code from builder as well
      
      return (
        <table className={`payslip-renderer-table ${field.style?.tableBorder === false ? 'no-border' : ''}`} style={getFieldStyle(field)}>
          <colgroup>
            {field.tableColumnWidths.map((width, index) => (
              <col key={index} style={{ width: `${width}%` }} />
            ))}
          </colgroup>
          <tbody>
            {field.tableCells.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, columnIndex) => {
                  if (isCellCoveredBySpan(field, rowIndex, columnIndex)) return null;

                  return (
                    <td key={columnIndex} colSpan={cell?.colSpan || 1} rowSpan={cell?.rowSpan || 1}>
                      {cell ? renderValue(cell, zone) : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return renderValue(field, zone);
  };

  if (!template) {
    return (
      <div className="payslip-renderer-empty">
        <div style={{ padding: '40px', textAlign: 'center', background: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
          <p style={{ fontSize: '16px', marginBottom: '10px', fontWeight: 'bold' }}>No Payslip Template Configured</p>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            Please create and configure a payslip template for this organization to view payslip details.
          </p>
          <p style={{ fontSize: '13px', color: '#999' }}>
            Go to Payslip Template Builder to create a new template and set it as active for your organization.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="payslip-renderer-sheet">
      {ZONES.map((zone) => (
        <div key={zone} className={`payslip-renderer-zone payslip-renderer-zone-${zone}`} style={getSectionStyle(zone)}>
          {layout[zone]?.map((field) => (
            <div key={field.templateItemId || field.fieldId} className={`payslip-renderer-field payslip-renderer-field-${field.type}`}>
              {renderItem(field, zone)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default PayslipTemplateRenderer;
